import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getUserInfo } from 'Utils';
import { addUserFavorite, deleteUserFavorite } from 'Services/library';
import { filterInterests } from 'Services/interests';
import { getUserRecommendations } from 'Services/recommendations';
import EmptyRecommendations from 'Components/profile/EmptyRecommendations';
import RecommendationsList from 'Components/profile/RecommendationsList';
// Fetch in excess of number of items displayed to account for potential duplicates, missing products, etc.
const FETCH_COUNT = 15;
const ITEM_LIMIT = 7;

const Recommendations = ({
  country,
  favoritesType,
  interestsBased,
  isVideo,
  language,
  strings,
  type,
  uim,
  verticalOnDesktop,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [itemList, setItemList] = useState([]);
  const [itemMap, setItemMap] = useState({});
  const [totalCount, setTotalCount] = useState(0);

  const reset = () => {
    setIsLoading(true);
    setHasError(false);
    setItemList([]);
    setItemMap({});
    setTotalCount(0);
  };

  const getLabel = (type) => {
    if (interestsBased) {
      const interest = filterInterests('programming')[type].name;
      return strings.interestsRecommendationsHeadingVariable.formatUimTranslation(
        interest
      );
    } else {
      return strings.recommendations;
    }
  };

  const getRecommendations = async () => {
    try {
      const { userId, userToken } = await getUserInfo(uim);
      const {
        isCancel,
        resourceCount,
        resources,
      } = await getUserRecommendations({
        favoritesType,
        interestsBased,
        limit: FETCH_COUNT,
        locale: `${language}-${country}`,
        type,
        userId,
        userToken,
      });

      // Fetching user tab library can be cancelled by quickly switching tabs,
      // the below ensures the loading state remains active in that case
      if (isCancel) return;

      const itemList = [];
      const itemMap = {};
      resources.splice(0, ITEM_LIMIT).forEach((item) => {
        if (!itemList.includes(item.id)) itemList.push(item.id);
        itemMap[item.id] = item;
      });
      setIsLoading(false);
      setHasError(false);
      setItemList(itemList);
      setItemMap(itemMap);
      setTotalCount(resourceCount);
    } catch ({ message }) {
      console.error(`Error fetching '${type}' recommended content: ${message}`);
      setIsLoading(false);
      setHasError(true);
    }
  };

  const updateFavoriteStatus = (id, status) => {
    setItemMap((itemMap) => {
      const item = itemMap[id];
      return {
        ...itemMap,
        [id]: {
          ...item,
          isFavorite: status,
        },
      };
    });
  };

  const addFavoriteRecommendation = async (contentId) => {
    try {
      updateFavoriteStatus(contentId, true);
      const { userId, userToken } = await getUserInfo(uim);
      await addUserFavorite({ contentId, userId, userToken });
    } catch ({ message }) {
      updateFavoriteStatus(contentId, false);
      console.error(
        `Error adding '${contentId}' to favorited content: ${message}`
      );
    }
  };

  const deleteFavoriteRecommendation = async (contentId) => {
    try {
      updateFavoriteStatus(contentId, false);
      const { userId, userToken } = await getUserInfo(uim);
      await deleteUserFavorite({ contentId, userId, userToken });
    } catch ({ message }) {
      updateFavoriteStatus(contentId, true);
      console.error(
        `Error removing '${contentId}' from favorited content: ${message}`
      );
    }
  };

  const handleFavoriteRecommendation = async (contentId) => {
    const { isFavorite } = itemMap[contentId];
    return isFavorite
      ? deleteFavoriteRecommendation(contentId)
      : addFavoriteRecommendation(contentId);
  };

  const renderRecommendations = () => {
    if (hasError || !(isLoading || totalCount)) {
      return <EmptyRecommendations strings={strings} />;
    }

    return (
      <RecommendationsList
        isLoading={isLoading}
        interestsBased={interestsBased}
        itemLimit={ITEM_LIMIT}
        itemList={itemList}
        itemMap={itemMap}
        isVideo={isVideo}
        onFavoriteRecommendation={handleFavoriteRecommendation}
        strings={strings}
        totalCount={totalCount}
        verticalOnDesktop={verticalOnDesktop}
      />
    );
  };

  useEffect(() => {
    reset();
    getRecommendations();
  }, [type]);

  return (
    <div
      className={`recommendations ${
        verticalOnDesktop ? 'recommendations--vertical-desktop' : ''
      }`}
    >
      <label className="row__label">{getLabel(type)}</label>
      {renderRecommendations()}
    </div>
  );
};

Recommendations.propTypes = {
  country: PropTypes.string.isRequired,
  favoritesType: PropTypes.string.isRequired,
  interestsBased: PropTypes.bool,
  isVideo: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  strings: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  uim: PropTypes.object.isRequired,
  verticalOnDesktop: PropTypes.bool,
};

Recommendations.defaultProps = {
  interestsBased: false,
  verticalOnDesktop: true,
};

export default Recommendations;
